import { DEFAULT_LOCALE, SUPPORTED_LOCALES, dynamicActivate, i18n } from '@/i18n'
import { LOCAL_STORAGE_KEY_UI_LOCALE } from '@/ui-kit/config'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { I18nProvider } from '@lingui/react'
import { PropsWithChildren, createContext, useCallback, useContext, useMemo } from 'react'

type LocaleProviderValue = {
  locale: string
  setLocale: (locale: string) => void
  availableLocales: string[]
}

const LocaleProviderContext = createContext({} as LocaleProviderValue)

export function LocaleProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [locale, setStoredLocale] = useLocalStorage<string>(LOCAL_STORAGE_KEY_UI_LOCALE, i18n.locale || DEFAULT_LOCALE)

  const setLocale = useCallback(
    (next: string) => {
      const target = SUPPORTED_LOCALES[next] ? next : DEFAULT_LOCALE
      setStoredLocale(target)
      void dynamicActivate(target)
    },
    [setStoredLocale]
  )

  const value = useMemo(
    () => ({ locale, setLocale, availableLocales: Object.keys(SUPPORTED_LOCALES) }),
    [locale, setLocale]
  )

  return (
    <LocaleProviderContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LocaleProviderContext.Provider>
  )
}

export function useLocale(): LocaleProviderValue {
  return useContext(LocaleProviderContext)
}
